import React from 'react';
import type {Appearance, Stripe} from '@stripe/stripe-js';
import {
    AddressElement as InternalAddressElement,
    Elements,
    PaymentElement as InternalPaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import {
    assertNotNullable,
    convertToStripeAmount,
    Price,
    Values,
} from '@wix/devzai-utils-common';

export const StripePaymentElementLocales = {
    Auto: 'auto',
    Arabic: 'ar',
    Bulgarian: 'bg',
    Czech: 'cs',
    Danish: 'da',
    German: 'de',
    Greek: 'el',
    English: 'en',
    EnglishAU: 'en-AU',
    EnglishCA: 'en-CA',
    EnglishNZ: 'en-NZ',
    EnglishGB: 'en-GB',
    Spanish: 'es',
    SpanishES: 'es-ES',
    Spanish419: 'es-419',
    Estonian: 'et',
    Finnish: 'fi',
    Filipino: 'fil',
    French: 'fr',
    FrenchCA: 'fr-CA',
    FrenchFR: 'fr-FR',
    Hebrew: 'he',
    Hungarian: 'hu',
    Croatian: 'hr',
    Indonesian: 'id',
    Italian: 'it',
    ItalianIT: 'it-IT',
    Japanese: 'ja',
    Korean: 'ko',
    Lithuanian: 'lt',
    Latvian: 'lv',
    Malay: 'ms',
    Maltese: 'mt',
    NorwegianBokmal: 'nb',
    Dutch: 'nl',
    Norwegian: 'no',
    Polish: 'pl',
    Portuguese: 'pt',
    PortugueseBR: 'pt-BR',
    Romanian: 'ro',
    Russian: 'ru',
    Slovak: 'sk',
    Slovenian: 'sl',
    Swedish: 'sv',
    Thai: 'th',
    Turkish: 'tr',
    Vietnamese: 'vi',
    Chinese: 'zh',
    ChineseHK: 'zh-HK',
    ChineseTW: 'zh-TW',
} as const;

export type StripePaymentElementLocales = Values<typeof StripePaymentElementLocales>;

export const StripePaymentElement = React.memo(function StripePaymentElement(props: StripePaymentElement.Props) {
    const {
        stripe,
        demoMode,
        appearance,
        returnUrl,
        children,
        fonts,
        price,
        locale,
    } = props;

    const {
        amount,
        currencyCode,
    } = convertToStripeAmount(price);

    return (
        <Elements
            options={{
                mode: 'payment' as const,
                appearance: appearance,
                fonts: fonts,
                amount: amount,
                currency: currencyCode,
                locale: locale,
            }}
            stripe={stripe}
        >
            <PaymentForm
                returnUrl={returnUrl}
                demoMode={demoMode}
                children={children}
            />
        </Elements>
    );
});

export namespace StripePaymentElement {
    export type ConfirmPaymentParams = {
        email?: string;
        name?: string;
        phone?: string;
        stripePaymentIntentClientSecret: string;
    };

    export type ConfirmPaymentResult = ConfirmPaymentResult.Success | ConfirmPaymentResult.Error;

    export namespace ConfirmPaymentResult {
        export type Error = {
            error: {
                type: string;
                message?: string;
            };
            status?: undefined;
            paymentIntentId?: undefined;
        };

        export type Success = {
            error?: undefined;
            status: PaymentStatus;
            paymentIntentId: string;
        };
    }

    export type ChildrenRenderProps = {
        confirmPayment: (params: ConfirmPaymentParams) => Promise<ConfirmPaymentResult>;
    };

    export type Props = {
        stripe: Promise<Stripe> | null;
        returnUrl: string;
        appearance?: Appearance;
        fonts?: Array<{ cssSrc: string }>;
        price: Price;
        children: (props: ChildrenRenderProps) => React.ReactElement;
        demoMode?: boolean;
        locale?: StripePaymentElementLocales;
    };

    export type PaymentStatus =
        | 'canceled'
        | 'processing'
        | 'requires_action'
        | 'requires_capture'
        | 'requires_confirmation'
        | 'requires_payment_method'
        | 'succeeded';
}

type PaymentFormProps = {
    returnUrl: StripePaymentElement.Props['returnUrl'];
    children: StripePaymentElement.Props['children'];
    demoMode: StripePaymentElement.Props['demoMode'];
};

const PaymentForm = React.memo(function PaymentForm(props: PaymentFormProps) {
    const {
        returnUrl,
        children,
        demoMode,
    } = props;
    const stripe = useStripe();
    const elements = useElements();

    if (demoMode) {
        return children({
            confirmPayment: () =>
                Promise.resolve({
                    status: 'succeeded',
                    paymentIntentId: 'demo',
                    error: undefined,
                }),
        });
    } else {
        const confirmPayment = async (props: {
            email?: string;
            name?: string;
            phone?: string;
            stripePaymentIntentClientSecret: string;
        }) => {
            const {
                email,
                name,
                phone,
                stripePaymentIntentClientSecret,
            } = props;
            if (!stripe || !elements) {
                assertNotNullable(stripe, 'stripe should be defined');
                assertNotNullable(elements, 'elements should be defined');
                return {
                    error: {
                        type: 'internal_error',
                        message: 'Something went wrong. Please refresh and try again.',
                    },
                };
            }

            const { error: validationError } = await elements.submit();
            if (validationError) {
                return {
                    error: validationError,
                };
            }

            const {
                error,
                paymentIntent,
            } = await stripe.confirmPayment({
                elements,
                clientSecret: stripePaymentIntentClientSecret,
                confirmParams: {
                    return_url: returnUrl,
                    receipt_email: email,
                    payment_method_data: {
                        billing_details: {
                            name,
                            email,
                            phone,
                        },
                    },
                },
                redirect: 'if_required',
            });

            return paymentIntent
                ? {
                    status: paymentIntent.status,
                    paymentIntentId: paymentIntent.id,
                }
                : {
                    error: {
                        message: error.message,
                        type: error.type,
                    },
                };
        };
        return children({ confirmPayment });
    }
});

export const PaymentElement = React.memo(function PaymentElement(props: {
    onLoaderStart?: () => void;
    onChange?: ({ inputsFilled }: { inputsFilled: boolean }) => void;
    onError: (error: { type: string; message?: string }) => void;
}) {
    const {
        onLoaderStart,
        onError,
        onChange,
    } = props;

    return (
        <InternalPaymentElement
            onChange={(event) => {
                onChange?.({ inputsFilled: event.complete });
            }}
            onLoaderStart={onLoaderStart}
            onLoadError={(error) => {
                onError(error.error);
            }}
        />
    );
});

export const AddressElement = React.memo(
    (props: {
        onLoaderStart?: () => void;
        collectPhone?: boolean;
        onError: (error: { type: string; message?: string }) => void;
        onChange: (value: {
            phone?: string;
            name: string;
            firstName?: string;
            lastName?: string;
            address: {
                country: string;
                line1: string;
                line2: string | null;
                city: string;
                state: string;
                postal_code: string;
            };
        }) => void;
    }) => {
        return (
            <InternalAddressElement
                onLoaderStart={props.onLoaderStart}
                options={{
                    mode: 'billing',
                    ...(props.collectPhone
                        ? {
                            fields: { phone: 'always' },
                            validation: { phone: { required: 'always' } },
                        }
                        : { fields: { phone: 'never' } }),
                }}
                onLoadError={(error) => {
                    props.onError(error.error);
                }}
                onChange={(event) => {
                    props.onChange(event.value);
                }}
            />
        );
    },
);
